import { ICaseloadVerdict } from './ICaseloadSearch';

export interface IParsedVerdict {
    verdict: ICaseloadVerdict;
    reason: string;
    sourceTitle?: string;
    // Whether the trailing line named a file outright. An extension says so; a bare title only
    // might, so the caller puts it back into the reason if no such document turns up.
    sourceTitleIsExplicit: boolean;
}

/**
 * One client's reply, reduced to the verdict, the reason behind it, and the document it came from.
 *
 * The model was asked for a verdict on its own first line; anything else means the shape was not
 * followed, and guessing at intent from prose is how a "no" quietly becomes a "yes".
 */
export const parseVerdict = (text: string): IParsedVerdict => {
    const lines = text.split('\n').map(line => line.trim()).filter(Boolean);
    const first = (lines[0] || '').replace(/[^a-z]/gi, '').toUpperCase();

    const verdict: ICaseloadVerdict | undefined =
        first === 'YES' ? 'yes' :
        first === 'NO' ? 'no' :
        first === 'UNCLEAR' ? 'unclear' :
        undefined;

    if (!verdict) {
        return { verdict: 'unclear', reason: lines.join(' '), sourceTitleIsExplicit: false };
    }

    // NONE is a sentinel for "no document", not prose, so it never belongs in the reason
    const rest = lines.slice(1).filter(line => !/^none[.]?$/i.test(line));
    const last = rest[rest.length - 1] || '';

    // The model is asked to put the file name on its own last line, but it does not reliably include
    // the extension: it answers "Advance Directive" as readily as "Advance Directive.pdf". Requiring
    // an extension therefore lost the document link and left the bare title stranded on the end of
    // the sentence. A trailing line is taken as a candidate when it reads like a title rather than a
    // sentence, and whether it really names a document is settled by looking for it among the
    // client's attachments rather than by guessing here.
    const hasExtension = /\.[a-z0-9]{2,5}$/i.test(last);
    const readsLikeTitle = last.length <= 60 && !/[.!?,;:]$/.test(last);
    // never consume the only line: that one is the reason
    const isCandidate = rest.length > 1 && (hasExtension || readsLikeTitle);
    const sourceTitle = isCandidate ? last : undefined;

    return {
        verdict,
        reason: (sourceTitle ? rest.slice(0, -1) : rest).join(' '),
        sourceTitle,
        sourceTitleIsExplicit: hasExtension
    };
};
